import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams.ts';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MIP_MANAGE_ROUTES } from '@dealstack/frontend/app/pages/Routing/routes.ts';
import { mipManagePath } from '@dealstack/frontend/app/pages/Routing/routes.utils.ts';
import { PageHeader } from '@dealstack/frontend/shared/layout/PageHeader/PageHeader.tsx';
import { useMIPManageContext } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/MIPManageContext';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { InvestmentDetailsStyles } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/InvestmentDetails.styles.tsx';
import { Button, Flex, Portal, Skeleton, Text } from '@mantine/core';
import { DetailsPagination } from '@dealstack/frontend/shared/components/DetailsPagination/DetailsPagination.tsx';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { useNavigateToMessages } from '@dealstack/frontend/app/pages/Project/MIP/Messages/hooks/useNavigateToMessages.ts';
import { InvestmentDetailsMenu } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentDetailsMenu/InvestmentDetailsMenu.tsx';
import { InvestmentDetailsSidebar } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentDetailsSidebar/InvestmentDetailsSidebar.tsx';
import { InvestmentDetailsContent } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentDetailsContent/InvestmentDetailsContent.tsx';
import { InvestmentDetailsProvider, useInvestmentDetails } from './InvestmentDetailsContext.tsx';
import { useInvestmentDetailsSummary } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/hooks/useInvestmentDetailsSummary.ts';
import { ConfirmDeleteDrawer } from '@dealstack/frontend/shared/components/ConfirmDeleteDrawer/ConfirmDeleteDrawer.tsx';
import { DATE_FORMATS, formatDateToUI } from '@dealstack/shared/lib/format/dates/date';
import { formatNumber } from '@dealstack/shared/lib/format/number';
import { useMipManageInvestmentDetailBreadcrumbs } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/utils/mipManageBreadcrumbs.utils.ts';
import { PageAbsoluteContainer } from '@dealstack/frontend/shared/layout/PageAbsoluteContainer/PageAbsoluteContainer.tsx';
import { InvestmentTargetDatesDrawer } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentTargetDatesDrawer/InvestmentTargetDatesDrawer.tsx';
import { useDetailsPagination } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/hooks/useDetailsPagination.ts';

const InvestmentDetailsInner = () => {
	const tMipManage = useTranslationMipManage();
	const { dealId, mipId, investmentId } = useRequiredParams('dealId', 'mipId', 'investmentId');
	const mipManageController = useMIPManageContext();
	const { orderedInvestmentIds, revokeTsaMutation, revokeDrawerOpen, setRevokeDrawerOpen } =
		useInvestmentDetails();
	const { data: investmentDetailsData } = useInvestmentDetailsSummary();
	const [targetDatesMenuOpen, setTargetDatesMenuOpen] = useState(false);

	const { navigateToMessages } = useNavigateToMessages(investmentDetailsData?.investor.id);
	const navigate = useNavigate();

	const programmeName = mipManageController?.mipData?.programmeName || 'Programme';

	useMipManageInvestmentDetailBreadcrumbs({ dealId, mipId, programmeName });

	const getInvestmentPath = useCallback(
		(id: string) =>
			mipManagePath(MIP_MANAGE_ROUTES.INVESTMENT_DETAILS, {
				dealId,
				mipId,
				investmentId: id,
			}),
		[dealId, mipId]
	);

	const investmentsNavigation = useDetailsPagination({
		ids: orderedInvestmentIds,
		currentId: investmentId,
		getPath: getInvestmentPath,
	});

	const pageTitle = useMemo(() => {
		if (!investmentDetailsData) {
			return (
				<Flex gap="0.5rem" align="center" wrap="wrap" pos="relative">
					{/* Reserves the real title's natural line-height so the header doesn't jump. */}
					<Text fz={32} fw={600} style={{ visibility: 'hidden' }}>
						&nbsp;
					</Text>

					<Skeleton
						height={32}
						width={360}
						radius="sm"
						style={{
							position: 'absolute',
							top: '50%',
							left: 0,
							transform: 'translateY(-50%)',
						}}
					/>
				</Flex>
			);
		}

		const investorName = investmentDetailsData.investor.displayName;
		const targetCompletionDate = investmentDetailsData.targetCompletionDate;
		const actualCompletionDate = investmentDetailsData.actualCompletionDate;

		const investingPart = tMipManage('investment_details.page_title.investment', {
			amount: formatNumber({
				value: investmentDetailsData.totalCost,
				currency: investmentDetailsData.currency,
				lockPrecision: true,
				currencyDisplay: 'symbol',
			}),
		});

		return (
			<Flex gap="0.5rem" align="center" wrap="wrap" mih={49.59}>
				<Text fz={32} fw={600} c="dark.8">
					{investorName}
				</Text>

				{investmentDetailsData.totalCost > 0 && (
					<Text fz={32} fw={600} c="dark.0">
						{investingPart}
					</Text>
				)}

				{actualCompletionDate && (
					<Text fz={32} fw={600} c="dark.0">
						{tMipManage('investment_details.page_title.actual_date', {
							date: formatDateToUI(
								new Date(actualCompletionDate),
								DATE_FORMATS.CALENDAR_DATE
							),
						})}
					</Text>
				)}

				{!actualCompletionDate && targetCompletionDate && (
					<Text fz={32} fw={600} c="dark.0">
						{tMipManage('investment_details.page_title.target_date', {
							date: formatDateToUI(
								new Date(targetCompletionDate),
								DATE_FORMATS.CALENDAR_DATE
							),
						})}
					</Text>
				)}
			</Flex>
		);
	}, [investmentDetailsData, tMipManage]);

	return (
		<PageAbsoluteContainer>
			<Portal target="#mip-manage-header-slot">
				<DetailsPagination
					label={tMipManage('investments_pagination.label')}
					counter={tMipManage('details_pagination.counter', {
						current: investmentsNavigation.currentIndex + 1,
						total: investmentsNavigation.total,
					})}
					prevDisabled={investmentsNavigation.prevDisabled}
					nextDisabled={investmentsNavigation.nextDisabled}
					onPrev={investmentsNavigation.goPrev}
					onNext={investmentsNavigation.goNext}
				/>
			</Portal>

			<InvestmentDetailsStyles>
				<PageHeader customTitle={pageTitle} hideBreadcrumbs>
					<Flex gap="0.5rem" align="center">
						<Button
							variant="default"
							onClick={() => navigateToMessages()}
							leftSection={<Icon variant="message-box" size="lg" />}
						>
							{tMipManage('investors.view_messages')}
						</Button>

						<InvestmentDetailsMenu
							onRevoke={() => setRevokeDrawerOpen(true)}
							openTargetDatesMenu={() => setTargetDatesMenuOpen(true)}
							onViewHistory={() =>
								navigate(
									mipManagePath(MIP_MANAGE_ROUTES.INVESTMENT_HISTORY, {
										dealId,
										mipId,
										investmentId,
									})
								)
							}
							canRevoke={
								investmentDetailsData ? !investmentDetailsData.isReleased : false
							}
							canSetTargetDates={
								investmentDetailsData ? !investmentDetailsData.isReleased : false
							}
						/>
					</Flex>
				</PageHeader>

				<div className="main">
					<div className="content">
						<InvestmentDetailsContent />
					</div>

					<div className="sidebar">
						<InvestmentDetailsSidebar />
					</div>
				</div>
			</InvestmentDetailsStyles>
			{investmentDetailsData && (
				<>
					<ConfirmDeleteDrawer
						opened={revokeDrawerOpen}
						onClose={() => {
							setRevokeDrawerOpen(false);
						}}
						title={tMipManage('investors.expanded_row.revoke_drawer.title')}
						promptText={tMipManage('investors.expanded_row.revoke_drawer.prompt_text')}
						confirmDeleteText={tMipManage(
							'investors.expanded_row.revoke_drawer.confirm_delete_text'
						)}
						inputLabel={tMipManage('investors.expanded_row.revoke_drawer.input_label')}
						inputPlaceholder={tMipManage(
							'investors.expanded_row.revoke_drawer.input_placeholder'
						)}
						wordMismatchErrorMessage={tMipManage(
							'investors.expanded_row.revoke_drawer.word_mismatch_error_message'
						)}
						deleteBtnText={tMipManage(
							'investors.expanded_row.revoke_drawer.delete_btn_text'
						)}
						cancelBtnText={tMipManage(
							'investors.expanded_row.revoke_drawer.cancel_btn_text'
						)}
						handleDelete={() =>
							revokeTsaMutation.mutate(investmentDetailsData.investmentWorkflowId)
						}
						deleteSuccess={revokeTsaMutation.isSuccess}
						deleteLoading={revokeTsaMutation.isPending}
						enableDeleteButtonOnlyOnConfirmDelete
					/>
					<InvestmentTargetDatesDrawer
						opened={targetDatesMenuOpen}
						onClose={() => setTargetDatesMenuOpen(false)}
						currentTargetStartDate={investmentDetailsData.targetStartDate}
						currentTargetCompletionDate={investmentDetailsData.targetCompletionDate}
					/>
				</>
			)}
		</PageAbsoluteContainer>
	);
};

export const InvestmentDetails = () => {
	return (
		<InvestmentDetailsProvider>
			<InvestmentDetailsInner />
		</InvestmentDetailsProvider>
	);
};
